/**
 * Central country configuration.
 * Changing country in Settings auto-applies these defaults.
 * EU countries use VAT; US uses Sales Tax.
 */

export interface CountryConfig {
  code: string;
  name: string;
  currency: string;
  vatPercent: number;
  taxLabel: string; // "VAT" / "Sales Tax" / "MwSt." etc.
  vatNumberLabel: string; // "VAT Number" / "Tax ID" / "USt-IdNr." etc.
  vatNumberPrefix: string; // e.g. "DE", "FR", "CHE-"
  rounding: string; // e.g. "0.01", "0.05"
  defaultLang: string; // ISO 639-1
  dateFormat: string; // display hint
  region: 'eu' | 'us' | 'other';
}

export const COUNTRIES: CountryConfig[] = [
  // ── EU ──────────────────────────────────────────────
  {
    code: 'DE',
    name: 'Germany',
    currency: 'EUR',
    vatPercent: 19,
    taxLabel: 'VAT',
    vatNumberLabel: 'USt-IdNr.',
    vatNumberPrefix: 'DE',
    rounding: '0.01',
    defaultLang: 'de',
    dateFormat: 'DD.MM.YYYY',
    region: 'eu',
  },
  {
    code: 'AT',
    name: 'Austria',
    currency: 'EUR',
    vatPercent: 20,
    taxLabel: 'VAT',
    vatNumberLabel: 'UID-Nr.',
    vatNumberPrefix: 'ATU',
    rounding: '0.01',
    defaultLang: 'de',
    dateFormat: 'DD.MM.YYYY',
    region: 'eu',
  },
  {
    code: 'FR',
    name: 'France',
    currency: 'EUR',
    vatPercent: 20,
    taxLabel: 'VAT',
    vatNumberLabel: 'N° TVA',
    vatNumberPrefix: 'FR',
    rounding: '0.01',
    defaultLang: 'fr',
    dateFormat: 'DD/MM/YYYY',
    region: 'eu',
  },
  {
    code: 'IT',
    name: 'Italy',
    currency: 'EUR',
    vatPercent: 22,
    taxLabel: 'VAT',
    vatNumberLabel: 'P.IVA',
    vatNumberPrefix: 'IT',
    rounding: '0.01',
    defaultLang: 'it',
    dateFormat: 'DD/MM/YYYY',
    region: 'eu',
  },
  {
    code: 'ES',
    name: 'Spain',
    currency: 'EUR',
    vatPercent: 21,
    taxLabel: 'VAT',
    vatNumberLabel: 'NIF-IVA',
    vatNumberPrefix: 'ES',
    rounding: '0.01',
    defaultLang: 'es',
    dateFormat: 'DD/MM/YYYY',
    region: 'eu',
  },
  {
    code: 'PT',
    name: 'Portugal',
    currency: 'EUR',
    vatPercent: 23,
    taxLabel: 'VAT',
    vatNumberLabel: 'NIF',
    vatNumberPrefix: 'PT',
    rounding: '0.01',
    defaultLang: 'pt',
    dateFormat: 'DD/MM/YYYY',
    region: 'eu',
  },
  {
    code: 'NL',
    name: 'Netherlands',
    currency: 'EUR',
    vatPercent: 21,
    taxLabel: 'VAT',
    vatNumberLabel: 'BTW-nr.',
    vatNumberPrefix: 'NL',
    rounding: '0.01',
    defaultLang: 'nl',
    dateFormat: 'DD-MM-YYYY',
    region: 'eu',
  },
  {
    code: 'BE',
    name: 'Belgium',
    currency: 'EUR',
    vatPercent: 21,
    taxLabel: 'VAT',
    vatNumberLabel: 'N° TVA',
    vatNumberPrefix: 'BE',
    rounding: '0.01',
    defaultLang: 'fr',
    dateFormat: 'DD/MM/YYYY',
    region: 'eu',
  },
  {
    code: 'PL',
    name: 'Poland',
    currency: 'PLN',
    vatPercent: 23,
    taxLabel: 'VAT',
    vatNumberLabel: 'NIP',
    vatNumberPrefix: 'PL',
    rounding: '0.01',
    defaultLang: 'pl',
    dateFormat: 'DD.MM.YYYY',
    region: 'eu',
  },
  {
    code: 'IE',
    name: 'Ireland',
    currency: 'EUR',
    vatPercent: 23,
    taxLabel: 'VAT',
    vatNumberLabel: 'VAT Reg.',
    vatNumberPrefix: 'IE',
    rounding: '0.01',
    defaultLang: 'en',
    dateFormat: 'DD/MM/YYYY',
    region: 'eu',
  },
  {
    code: 'SE',
    name: 'Sweden',
    currency: 'SEK',
    vatPercent: 25,
    taxLabel: 'VAT',
    vatNumberLabel: 'Momsreg.nr.',
    vatNumberPrefix: 'SE',
    rounding: '0.01',
    defaultLang: 'en',
    dateFormat: 'YYYY-MM-DD',
    region: 'eu',
  },
  {
    code: 'DK',
    name: 'Denmark',
    currency: 'DKK',
    vatPercent: 25,
    taxLabel: 'VAT',
    vatNumberLabel: 'CVR',
    vatNumberPrefix: 'DK',
    rounding: '0.01',
    defaultLang: 'en',
    dateFormat: 'DD.MM.YYYY',
    region: 'eu',
  },
  {
    code: 'FI',
    name: 'Finland',
    currency: 'EUR',
    vatPercent: 25.5,
    taxLabel: 'VAT',
    vatNumberLabel: 'ALV-nro',
    vatNumberPrefix: 'FI',
    rounding: '0.01',
    defaultLang: 'en',
    dateFormat: 'DD.MM.YYYY',
    region: 'eu',
  },
  {
    code: 'CZ',
    name: 'Czech Republic',
    currency: 'CZK',
    vatPercent: 21,
    taxLabel: 'VAT',
    vatNumberLabel: 'DIČ',
    vatNumberPrefix: 'CZ',
    rounding: '0.01',
    defaultLang: 'en',
    dateFormat: 'DD.MM.YYYY',
    region: 'eu',
  },
  {
    code: 'GR',
    name: 'Greece',
    currency: 'EUR',
    vatPercent: 24,
    taxLabel: 'VAT',
    vatNumberLabel: 'ΑΦΜ',
    vatNumberPrefix: 'EL',
    rounding: '0.01',
    defaultLang: 'en',
    dateFormat: 'DD/MM/YYYY',
    region: 'eu',
  },
  // ── Switzerland (non-EU, but keep for existing users) ──
  {
    code: 'CH',
    name: 'Switzerland',
    currency: 'CHF',
    vatPercent: 8.1,
    taxLabel: 'VAT',
    vatNumberLabel: 'MWST-Nr.',
    vatNumberPrefix: 'CHE-',
    rounding: '0.05',
    defaultLang: 'de',
    dateFormat: 'DD.MM.YYYY',
    region: 'other',
  },
  // ── United States ──────────────────────────────────
  {
    code: 'US',
    name: 'United States',
    currency: 'USD',
    vatPercent: 0,
    taxLabel: 'Sales Tax',
    vatNumberLabel: 'Tax ID (EIN)',
    vatNumberPrefix: '',
    rounding: '0.01',
    defaultLang: 'en',
    dateFormat: 'MM/DD/YYYY',
    region: 'us',
  },
];

/** Lookup by ISO code. Falls back to US. */
export function getCountry(code: string): CountryConfig {
  return (
    COUNTRIES.find(c => c.code === code) ??
    COUNTRIES.find(c => c.code === 'US')!
  );
}

/** Apply rounding from settings string. e.g. "0.05" → round to nearest 0.05 */
export function applyRounding(value: number, rounding: string): number {
  const r = parseFloat(rounding);
  if (!r || r <= 0) return value;
  return Math.round(value / r) * r;
}
